import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  Switch,
  type TanstackReactTable,
} from "@devhop/ui";
import { Edit, Folder, Trash2 } from "lucide-react";
import type { CategoriesListResult } from "@/modules/form-center/domain/types/categories";

type CategoryRow = CategoriesListResult["data"][number];

type CategoriesListProps = {
  data: CategoryRow[];
  isLoading?: boolean;
  canDelete?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (category: CategoryRow) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  onDelete?: (category: CategoryRow) => void | Promise<void>;
};

export function CategoriesList({
  data,
  isLoading,
  canDelete,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onToggleActive,
  onDelete,
}: CategoriesListProps) {
  const columns: TanstackReactTable.ColumnDef<CategoryRow>[] = [
    createSortableColumn<CategoryRow>("name", "ຊື່ຫມວດຫມູ່", {
      size: 200,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded border bg-muted">
            <Folder className="h-4 w-4" />
          </div>
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    }),
    createSortableColumn<CategoryRow>("description", "ຄຳອະທິບາຍ", {
      size: 300,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.description || "-"}
        </span>
      ),
    }),
    createSortableColumn<CategoryRow>("activeFormsCount", "ຟອມທີ່ໃຊ້ງານ", {
      size: 120,
      cell: ({ row }) => (
        <Badge variant="secondary">{row.original.activeFormsCount ?? 0}</Badge>
      ),
    }),
    createSortableColumn<CategoryRow>("orderIndex", "ລໍາດັບ", {
      size: 100,
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.orderIndex}</Badge>
      ),
    }),
    createSortableColumn<CategoryRow>("isActive", "ສະຖານະ", {
      size: 150,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Switch
            checked={row.original.isActive}
            onCheckedChange={(checked) =>
              onToggleActive(row.original.id, checked)
            }
          />
          <span className="text-sm">
            {row.original.isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
          </span>
        </div>
      ),
    }),
    {
      size: canDelete && onDelete ? 120 : 80,
      id: "actions",
      cell: ({ row }: { row: { original: CategoryRow } }) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(row.original)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          {canDelete && onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => void onDelete(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable<CategoryRow, unknown>
      noDataMessage="ບໍ່ພົບຫມວດຫມູ່"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
